import { makeFinalFormValidator, OverlayAnnotations } from '@readcloud/common';
import React, { useEffect, useState } from 'react';

import { Button, Collapse, Fade } from '@readcloud/component-library';
import { Annotation, UserPrefState } from '@readcloud/data';
import { Form } from 'react-final-form';
import yup from 'yup';
import { Forms } from '../';
import { Subtle } from '@readcloud/ui/typography';
export type AnnotationEditorFormProps = {
  submitLabel?: string;
  onCancel?: () => void;
  showPublisherOptions: boolean;
  showDescription?: boolean;
  onSavePreferences?: (
    type: string,
    data: Partial<OverlayAnnotations.Annotation['data']>
  ) => void;
  annoPrefs?: UserPrefState['annoPrefs'];
};

/**
 * Use this component creator function (HOC) to
 */
export function makeAnnotationEditor<
  Anno extends Annotation,
  FormValues extends Annotation['data'],
  EditorProps extends AnnotationEditorFormProps
>(params: {
  FormComponent: React.ComponentType<EditorProps>;
  schema: yup.ISchema<FormValues>;
}) {
  return React.memo(function Editor(
    props: {
      annotation: Anno;
      onChange: (annotation: Anno) => void;
      className?: string;
      autoSubmit?: boolean;
    } & EditorProps
  ) {
    const validate = makeFinalFormValidator(params.schema);

    const [disableSavePreferences, setDisableSavePreferences] = useState(true);
    const [currentDataValues, setCurrentDataValues] =
      useState<Partial<OverlayAnnotations.Annotation['data']>>();

    // get just the preference values that need to be checked to see if the button should be disabled.
    useEffect(() => {
      switch (props.annotation.type) {
        case 'Media': {
          //just get the fields that we want to check against.
          const { variant } = props.annotation
            .data as OverlayAnnotations.MediaAnnotation['data'];
          setCurrentDataValues({ variant });
          break;
        }
        case 'HTML': {
          //just get the fields that we want to check against.
          setCurrentDataValues({});
          break;
        }
        case 'Image': {
          //just get the fields that we want to check against.
          setCurrentDataValues({});
          break;
        }
        case 'Circle': {
          //just get the fields that we want to check against.
          const { strokeWidth, fill, stroke } = props.annotation
            .data as OverlayAnnotations.CircleAnnotation['data'];

          setCurrentDataValues({
            strokeWidth,
            fill,
            stroke,
          });
          break;
        }
        case 'Rectangle': {
          //just get the fields that we want to check against.
          const { strokeWidth, fill, stroke } = props.annotation
            .data as OverlayAnnotations.RectangleAnnotation['data'];
          setCurrentDataValues({
            strokeWidth,
            fill,
            stroke,
          });
          break;
        }
        case 'OverlayText': {
          //just get the fields that we want to check against.
          const {
            text,
            fill,
            textAlign,
            fontFamily,
            fontSize,
            fontWeight,
            lineHeight,
            overflowWrap,
            wordBreak,
          } = props.annotation
            .data as OverlayAnnotations.OverlayTextAnnotation['data'];

          setCurrentDataValues({
            text,
            fill,
            textAlign,
            fontFamily,
            fontSize,
            fontWeight,
            lineHeight,
            overflowWrap,
            wordBreak,
          });
          break;
        }
        case 'Path': {
          const { strokeWidth, stroke } = props.annotation
            .data as OverlayAnnotations.PathAnnotation['data'];

          setCurrentDataValues({
            strokeWidth,
            stroke,
          });
          break;
        }
      }
    }, [props.annotation.data, props.annotation.type]);

    const submit = React.useCallback(
      (values: FormValues) => {
        //setDisableSavePreferences(false);

        props.onChange(
          Forms.commonElements.formValuesToAnnotation(props.annotation, values)
        );
      },
      [props.annotation, props.onChange]
    );

    useEffect(() => {
      const disabled =
        currentDataValues &&
        Object.keys(currentDataValues).every((annoKey) => {
          return (
            props.annoPrefs &&
            props.annoPrefs[props.annotation.type] &&
            (props.annoPrefs[props.annotation.type] as any)[annoKey] ===
              (currentDataValues as any)[annoKey]
          );
        });

      setDisableSavePreferences(disabled || false);
    }, [currentDataValues, props.annoPrefs]);

    const initialFormValues = React.useMemo(
      () => ({
        ...props.annotation.data,
        publisherOptions: props.annotation.options
          ? {
              publisherType: props.annotation.options.type === 'Publisher',
              status: props.annotation.options.status || 'Draft',
            }
          : undefined,
      }),
      [props.annotation.data]
    );

    return (
      <div style={{ width: 'auto' }} className={props.className}>
        <Form
          validate={validate}
          onSubmit={submit}
          initialValues={initialFormValues}
          //the when the initial form values changed becuase of the form auto-submitting on any onChange, it would reinitialise and cause another onChange event to trigger - causing an infinite loop.
          //the only reason this wasn't crashing the app is because the autosubmit is on a 300ms debounce.
          //these 2 props seem to be required to stop that from happening.
          keepDirtyOnReinitialize
          //The initalValuesEqual prop is meant to take a deepEquals function that it uses on the previous/current values. Since we don't use the same form for different annotations
          //(we dont swap another anno's values into the current form at any point), I think it's safe to assume that the previous values should always be the same as the current values here,
          // and we can save on some computation.
          initialValuesEqual={() => true}
        >
          {(formProps) => (
            <>
              <Forms.AutoSubmit enabled={props.autoSubmit} />
              <params.FormComponent {...props} />
              <hr />
              <div className="rc-flex rc-justify-end">
                <div className="rc-flex rc-flex-col rc-items-end">
                  <Collapse timeout={150} isOpen={!disableSavePreferences}>
                    <Fade timeout={150} in={!disableSavePreferences}>
                      <div className="rc-flex rc-justify-between rc-w-full rc-mt-2">
                        <Button
                          size="sm"
                          onClick={() => {
                            props.onSavePreferences &&
                              props.onSavePreferences(
                                props.annotation.type,
                                formProps.form.getState().values
                              );
                          }}
                          disabled={disableSavePreferences}
                        >
                          Save as Preference
                        </Button>
                      </div>
                    </Fade>
                  </Collapse>
                  <Collapse timeout={150} isOpen={disableSavePreferences}>
                    <Fade timeout={150} in={disableSavePreferences}>
                      <Subtle>Using Saved Preferences</Subtle>
                    </Fade>
                  </Collapse>
                </div>
              </div>
            </>
          )}
        </Form>
      </div>
    );
  });
}
